import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { ArrowLeft, CalendarClock, History, LockKeyhole, RotateCcw, X } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  createHomeServiceReminder,
  dismissHomeServiceReminder,
  listDismissedHomeServiceReminders,
  listHomeServiceReminderHistory,
  listHomeServiceReminders,
  listHomes,
  restoreHomeServiceReminder,
  updateHomeServiceReminder,
} from "@/lib/homes.functions";
import { getMyMembership } from "@/lib/memberships.functions";

export const Route = createFileRoute("/_authenticated/home/maintenance")({
  validateSearch: (search: Record<string, unknown>) => ({ homeId: typeof search['homeId'] === "string" ? search['homeId'] : undefined }),
  head: () => ({
    meta: [
      { title: "Maintenance — Shields" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: MaintenancePage,
});

function MaintenancePage() {
  const { homeId: requestedHomeId } = Route.useSearch();
  const client = useQueryClient();
  const membership = useQuery({ queryKey: ["my-membership"], queryFn: () => getMyMembership() });
  const homes = useQuery({ queryKey: ["homes"], queryFn: () => listHomes() });
  const reminders = useQuery({ queryKey: ["home-reminders"], queryFn: () => listHomeServiceReminders() });
  const dismissed = useQuery({ queryKey: ["home-reminders-dismissed"], queryFn: () => listDismissedHomeServiceReminders() });
  const history = useQuery({ queryKey: ["home-reminder-history"], queryFn: () => listHomeServiceReminderHistory() });
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedHomeId = selectedId ?? homes.data?.find((home) => home.id === requestedHomeId)?.id ?? "";
  const activeReminders = (reminders.data ?? []).filter((reminder: any) => !selectedHomeId || reminder.home_id === selectedHomeId);
  const dismissedReminders = (dismissed.data ?? []).filter((reminder: any) => !selectedHomeId || reminder.home_id === selectedHomeId);
  const reminderHistory = (history.data ?? []).filter((event: any) => !selectedHomeId || event.home_service_reminders?.home_id === selectedHomeId);

  const entitlements = membership.data?.plan?.entitlement_keys ?? [];
  const entitled = entitlements.includes("home_reminders");
  const today = new Date().toISOString().slice(0, 10);

  async function refresh() {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["home-reminders"] }),
      client.invalidateQueries({ queryKey: ["home-reminders-dismissed"] }),
      client.invalidateQueries({ queryKey: ["home-reminder-history"] }),
      client.invalidateQueries({ queryKey: ["customer-attention"] }),
    ]);
  }

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setBusy(true);
    try {
      const result = await createHomeServiceReminder({
        data: {
          homeId: String(data.get("homeId") || "") || undefined,
          title: String(data.get("title") || ""),
          dueDate: String(data.get("dueDate") || ""),
          notifyDate: String(data.get("notifyDate") || "") || undefined,
          note: String(data.get("note") || "") || undefined,
        },
      });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      form.reset();
      toast.success("Maintenance reminder added");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not add this reminder");
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(event: FormEvent<HTMLFormElement>, reminderId: string) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    try {
      const result = await updateHomeServiceReminder({
        data: {
          reminderId,
          title: String(data.get("title") || ""),
          dueDate: String(data.get("dueDate") || ""),
          notifyDate: String(data.get("notifyDate") || ""),
          note: String(data.get("note") || "") || undefined,
        },
      });
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      setEditingId(null);
      toast.success("Reminder updated");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update this reminder");
    } finally {
      setBusy(false);
    }
  }

  async function dismiss(reminderId: string) {
    setBusy(true);
    try {
      const result = await dismissHomeServiceReminder({ data: { reminderId } });
      if (!result.ok) { toast.error(result.error); return; }
      toast.success("Reminder dismissed");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not dismiss this reminder");
    } finally {
      setBusy(false);
    }
  }

  async function restore(reminderId: string) {
    setBusy(true);
    try {
      const result = await restoreHomeServiceReminder({ data: { reminderId } });
      if (!result.ok) { toast.error(result.error); return; }
      toast.success("Reminder restored");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not restore this reminder");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-24">
      <AppHeader workspace="customer" />
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <Link to="/home" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> My Home
        </Link>
        <header className="mt-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#c99522]">Your home</p>
          <h1 className="mt-1 text-3xl font-extrabold text-[#061b3a]">Reminders</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Upcoming maintenance dates you set, plus dates backed by genuine completed service records. Shields never invents a servicing requirement.
          </p>
        </header>

        {membership.isError || homes.isError ? (
          <section className="card-surface mt-8 p-5">
            <p>We couldn’t check your membership or properties.</p>
            <Button type="button" variant="outline" className="mt-3" onClick={() => { if (membership.isError) membership.refetch(); if (homes.isError) homes.refetch(); }}>Try again</Button>
          </section>
        ) : membership.isLoading || homes.isLoading ? (
          <p className="mt-8 text-sm text-muted-foreground">Loading your home reminders…</p>
        ) : requestedHomeId && !homes.data?.some((home) => home.id === requestedHomeId) ? (
          <section className="card-surface mt-8 p-5"><p>This property is not available to your account.</p><Link to="/home" className="btn-secondary-hero mt-3 inline-flex">Your properties</Link></section>
        ) : !entitled ? (
          <section className="business-locked mt-8">
            <LockKeyhole />
            <div>
              <h2>Maintenance reminders are included with Shields Home and above</h2>
              <p>Essential remains fully usable for booking, payments, messaging, reviews and job history.</p>
              <Link to="/plans">Compare memberships</Link>
            </div>
          </section>
        ) : entitled ? (
          <>
            <section className="mt-6 rounded-[1.75rem] border border-[#e1e8f0] bg-white p-5 shadow-sm">
              <h2 className="text-lg font-extrabold text-[#061b3a]">Add a reminder</h2>
              <form onSubmit={create} className="mt-4 grid gap-4">
                <label className="grid gap-1.5 text-sm font-semibold">
                  Property
                  <select name="homeId" value={selectedHomeId} onChange={(event) => setSelectedId(event.target.value)} className="min-h-11 rounded-md border border-input bg-background px-3">
                    <option value="">General home reminder</option>
                    {(homes.data ?? []).map((home) => <option key={home.id} value={home.id}>{home.label} · {home.postcode}</option>)}
                  </select>
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="reminder-title">What should Shields remind you about?</Label>
                    <Input id="reminder-title" name="title" required minLength={2} maxLength={120} placeholder="e.g. Boiler service anniversary" className="mt-1.5" />
                  </div>
                  <div>
                    <Label htmlFor="reminder-due">Due date</Label>
                    <Input id="reminder-due" name="dueDate" type="date" min={today} required className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="reminder-notify">Notify me on</Label>
                  <Input id="reminder-notify" name="notifyDate" type="date" min={today} className="mt-1.5" />
                  <p className="mt-1 text-xs text-muted-foreground">Leave blank to notify on the due date.</p>
                </div>
                <div>
                  <Label htmlFor="reminder-note">Note (optional)</Label>
                  <Input id="reminder-note" name="note" maxLength={500} placeholder="e.g. Last serviced by Smith Heating" className="mt-1.5" />
                </div>
                <Button type="submit" disabled={busy} className="min-h-12 bg-[#e1b64f] font-extrabold text-[#061b3a] hover:bg-[#d8a82f]">{busy ? "Saving…" : "Add Reminder"}</Button>
              </form>
            </section>

            <section className="mt-7">
              <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#0b3568]" /><h2 className="text-lg font-extrabold text-[#061b3a]">Upcoming</h2></div>
              {reminders.isError ? (
                <div className="mt-4 rounded-xl border border-border p-4">
                  <p className="text-sm text-muted-foreground">We couldn’t load your reminders.</p>
                  <Button type="button" size="sm" variant="outline" onClick={() => reminders.refetch()} className="mt-3">Try again</Button>
                </div>
              ) : reminders.isLoading ? (
                <p className="mt-4 text-sm text-muted-foreground">Loading reminders…</p>
              ) : activeReminders.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">No active reminders yet.</p>
              ) : (
                <div className="mt-4 space-y-3">
                  {activeReminders.map((reminder: any) => (
                    <article key={reminder.id} className="rounded-2xl border border-[#e1e8f0] bg-white p-4">
                      {editingId === reminder.id ? (
                        <form onSubmit={(event) => saveEdit(event, reminder.id)} className="grid gap-3">
                          <Input name="title" defaultValue={reminder.title} minLength={2} maxLength={120} required />
                          <div className="grid gap-3 sm:grid-cols-2">
                            <Input name="dueDate" type="date" min={today} defaultValue={reminder.due_date} required />
                            <Input name="notifyDate" type="date" min={today} defaultValue={reminder.notify_date ?? reminder.due_date} required />
                          </div>
                          <Input name="note" defaultValue={reminder.note ?? ""} maxLength={500} placeholder="Note" />
                          <div className="flex gap-2">
                            <Button type="submit" size="sm" disabled={busy}>Save</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                          </div>
                        </form>
                      ) : (
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <strong>{reminder.title}</strong>
                            <p className="mt-1 text-sm text-muted-foreground">
                              Due {new Date(reminder.due_date + "T12:00:00").toLocaleDateString("en-GB")}
                              {" · "}notify {new Date((reminder.notify_date ?? reminder.due_date) + "T12:00:00").toLocaleDateString("en-GB")}
                              {" · "}{reminder.source === "customer_set" ? "set by you" : "from recorded service history"}
                            </p>
                            {reminder.note && <p className="mt-1 text-sm">{reminder.note}</p>}
                          </div>
                          <div className="flex gap-2">
                            <Button type="button" size="sm" variant="outline" onClick={() => setEditingId(reminder.id)}>Edit</Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => dismiss(reminder.id)}><X className="mr-1 h-4 w-4" />Dismiss</Button>
                          </div>
                        </div>
                      )}
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="dashboard-section">
              <div className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-primary" /><h2 className="text-lg font-extrabold">Dismissed reminders</h2></div>
              {dismissed.isError ? <div className="mt-4 text-sm"><p>We couldn’t load dismissed reminders.</p><Button type="button" variant="outline" className="mt-3" onClick={() => dismissed.refetch()}>Try again</Button></div> : dismissed.isLoading ? <p className="mt-4 text-sm text-muted-foreground">Loading dismissed reminders…</p> : dismissedReminders.length === 0 ? <p className="mt-4 text-sm text-muted-foreground">Nothing dismissed.</p> : (
                <div className="mt-4 space-y-2">
                  {dismissedReminders.slice(0, 20).map((reminder: any) => (
                    <div key={reminder.id} className="reference-row">
                      <span><strong>{reminder.title}</strong><small>Due {reminder.due_date}</small></span>
                      <Button type="button" size="sm" variant="outline" onClick={() => restore(reminder.id)}>Restore</Button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="dashboard-section">
              <div className="flex items-center gap-2"><History className="h-5 w-5 text-primary" /><h2 className="text-lg font-extrabold">Reminder history</h2></div>
              {history.isError ? <div className="mt-4 text-sm"><p>We couldn’t load reminder history.</p><Button type="button" variant="outline" className="mt-3" onClick={() => history.refetch()}>Try again</Button></div> : history.isLoading ? <p className="mt-4 text-sm text-muted-foreground">Loading reminder history…</p> : reminderHistory.length === 0 ? <p className="mt-4 text-sm text-muted-foreground">No reminder changes for this property yet.</p> : <div className="mt-4 space-y-2">
                {reminderHistory.slice(0, 30).map((event: any) => (
                  <div key={event.id} className="reference-row">
                    <span>
                      <strong className="capitalize">{event.event_type.replaceAll("_", " ")}</strong>
                      <small>{event.home_service_reminders?.title ?? "Maintenance reminder"} · {new Date(event.created_at).toLocaleString("en-GB")}</small>
                    </span>
                  </div>
                ))}
              </div>}
            </section>
          </>
        ) : null}
      </main>
    </div>
  );
}
